import React from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { typeShortLabel } from '../common/assessment';
import { parseJSONString, getStatusColor, getAnswerColor, detectChanges } from '../common/auditFormat';

/*
 * Word-level diff (LCS) so the audit view can highlight exactly what changed
 * inside a paragraph instead of tinting the whole block. Reason/evidence
 * texts are short (sentences, not documents), so the O(n*m) table is cheap.
 */
const diffWords = (oldText, newText) => {
  const a = String(oldText || '').split(/\s+/).filter(Boolean);
  const b = String(newText || '').split(/\s+/).filter(Boolean);
  const m = a.length;
  const n = b.length;
  const lcs = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      ops.push({ t: 'same', w: a[i] });
      i++; j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ t: 'del', w: a[i] });
      i++;
    } else {
      ops.push({ t: 'add', w: b[j] });
      j++;
    }
  }
  while (i < m) { ops.push({ t: 'del', w: a[i] }); i++; }
  while (j < n) { ops.push({ t: 'add', w: b[j] }); j++; }

  // Merge runs of the same type so the DOM stays small and highlights read
  // as phrases rather than per-word confetti.
  const merged = [];
  ops.forEach((op) => {
    const last = merged[merged.length - 1];
    if (last && last.t === op.t) last.w += ` ${op.w}`;
    else merged.push({ ...op });
  });
  return merged;
};

// Renders one side of a diffed text: the old card shows kept + removed
// phrases (struck through red), the new card shows kept + added (green).
const DiffText = ({ oldText, newText, side, inline = false }) => {
  const parts = diffWords(oldText, newText).filter((p) =>
    p.t === 'same' || (side === 'old' ? p.t === 'del' : p.t === 'add')
  );
  const body = parts.map((p, idx) => (
    <React.Fragment key={idx}>
      {p.t === 'same' ? (
        <span>{p.w}</span>
      ) : p.t === 'del' ? (
        <span className="bg-red-100 text-red-800 line-through decoration-red-400 rounded px-0.5">{p.w}</span>
      ) : (
        <span className="bg-green-100 text-green-800 rounded px-0.5 font-medium">{p.w}</span>
      )}{' '}
    </React.Fragment>
  ));
  if (inline) return <span>{body}</span>;
  return <p className="text-sm text-gray-700 mt-1 leading-relaxed">{body}</p>;
};

// The card's structured sections below only know question-shaped values. Call
// metadata overrides (DNIS, ConversationId, Agent, ...) carry plain strings or
// numbers, which used to fall through every section and render an empty card.
const QUESTION_KEYS = ['status', 'answer', 'score', 'reason', 'applicabilitySteps', 'evidence'];
const isQuestionShaped = (p) =>
  p && typeof p === 'object' && !Array.isArray(p) && QUESTION_KEYS.some((k) => p[k] != null);
const plainDisplay = (p) => {
  if (p == null) return '';
  if (typeof p !== 'object') return String(p);
  if (p.error === 'Invalid JSON') return p.raw == null ? '' : String(p.raw);
  return JSON.stringify(p, null, 2);
};

export const FieldComparison = ({ fieldName, oldValue, newValue, isOld = false }) => {
  const oldParsed = parseJSONString(oldValue);
  const newParsed = parseJSONString(newValue);
  const parsed = isOld ? oldParsed : newParsed;
  const other = isOld ? newParsed : oldParsed;
  const changes = detectChanges(oldValue, newValue);
  const questionShaped = isQuestionShaped(oldParsed) || isQuestionShaped(newParsed);
  const oldPlain = plainDisplay(oldParsed);
  const newPlain = plainDisplay(newParsed);
  const plainChanged = oldPlain !== newPlain;
  const isChanged = questionShaped ? changes.length > 0 : plainChanged;
  const sidePlain = isOld ? oldPlain : newPlain;

  const statusChanged = changes.includes('status');
  const scoreChanged = changes.includes('score');
  const answerChanged = changes.includes('answer');
  const reasonChanged = changes.includes('reason');
  const stepsChanged = changes.includes('steps');
  const agentEvidenceChanged =
    (oldParsed.evidence?.agent || '') !== (newParsed.evidence?.agent || '');
  const customerEvidenceChanged =
    (oldParsed.evidence?.customer || '') !== (newParsed.evidence?.customer || '');

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        isChanged ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h4 className="text-sm font-semibold text-gray-800 leading-snug">{fieldName}</h4>
        {isChanged && (
          <span className="status-badge status-medium flex-shrink-0">Changed</span>
        )}
      </div>

      {!questionShaped && (
        sidePlain === '' ? (
          <p className="text-sm text-gray-400 italic">empty</p>
        ) : plainChanged ? (
          <DiffText oldText={oldPlain} newText={newPlain} side={isOld ? 'old' : 'new'} />
        ) : (
          <p className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap">{sidePlain}</p>
        )
      )}

      {parsed.status && (
        <div className={`mb-2 ${statusChanged ? 'bg-amber-50 rounded-md px-2 py-1.5' : ''}`}>
          <span className="text-xs font-medium text-gray-500">Status</span>
          {/* On the new card a changed value renders as old → new, so the
              exact transition is visible without eyeballing both columns. */}
          {statusChanged && !isOld && other.status && (
            <>
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium border opacity-60 line-through decoration-gray-400 bg-gray-100 text-gray-500 border-gray-200">
                {other.status}
              </span>
              <FaArrowRight className="inline mx-1.5 text-[10px] text-gray-400" />
            </>
          )}
          <span className={`${statusChanged && !isOld ? '' : 'ml-2 '}px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(parsed.status)}`}>
            {parsed.status}
          </span>
        </div>
      )}

      {parsed.answer && (
        <div className={`mb-2 ${answerChanged ? 'bg-amber-50 rounded-md px-2 py-1.5' : ''}`}>
          <span className="text-xs font-medium text-gray-500">Answer</span>
          {answerChanged && !isOld && other.answer && (
            <>
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium border opacity-60 line-through decoration-gray-400 bg-gray-100 text-gray-500 border-gray-200">
                {other.answer.toUpperCase()}
              </span>
              <FaArrowRight className="inline mx-1.5 text-[10px] text-gray-400" />
            </>
          )}
          <span className={`${answerChanged && !isOld ? '' : 'ml-2 '}px-2 py-0.5 rounded-full text-xs font-medium border ${getAnswerColor(parsed.answer, parsed.type)}`}>
            {parsed.answer.toUpperCase()}
          </span>
          {parsed.type && (
            <span className="ml-2 text-xs text-gray-400">({typeShortLabel(parsed.type)})</span>
          )}
        </div>
      )}

      {parsed.score && (
        <div className={`mb-2 ${scoreChanged ? 'bg-amber-50 rounded-md px-2 py-1.5' : ''}`}>
          <span className="text-xs font-medium text-gray-500">Score</span>
          {scoreChanged && !isOld && other.score && (
            <>
              <span className="ml-2 text-sm text-gray-400 line-through decoration-gray-400">{other.score}</span>
              <FaArrowRight className="inline mx-1.5 text-[10px] text-gray-400" />
              <span className="font-semibold text-cloudza-600">{parsed.score}</span>
            </>
          )}
          {!(scoreChanged && !isOld && other.score) && (
            <span className="ml-2 font-semibold text-cloudza-600">{parsed.score}</span>
          )}
        </div>
      )}

      {parsed.reason && (
        <div className={`mb-2 ${reasonChanged ? 'bg-amber-50 rounded-md px-2 py-1.5' : ''}`}>
          <span className="text-xs font-medium text-gray-500">Reason</span>
          {reasonChanged ? (
            <DiffText
              oldText={oldParsed.reason}
              newText={newParsed.reason}
              side={isOld ? 'old' : 'new'}
            />
          ) : (
            <p className="text-sm text-gray-700 mt-1 leading-relaxed">{parsed.reason}</p>
          )}
        </div>
      )}

      {/* Pre-check steps, highlighted when a reviewer corrected one. Without this a
          steps-only override read as no change at all. */}
      {Array.isArray(parsed.applicabilitySteps) && parsed.applicabilitySteps.length > 0 && (
        <div className={`mb-2 ${stepsChanged ? 'bg-amber-50 rounded-md px-2 py-1.5' : ''}`}>
          <span className="text-xs font-medium text-gray-500">Pre-checks</span>
          <ol className="mt-1 space-y-1">
            {parsed.applicabilitySteps.map((step, i) => (
              <li key={step?.step ?? i} className="text-xs text-gray-700">
                <span className="font-medium">Step {step?.step ?? i + 1}:</span>{' '}
                <span className="uppercase">{step?.result || 'not reached'}</span>
                {step?.reason && <span className="text-gray-600"> — {step.reason}</span>}
                {step?.overriddenBy && (
                  <span className="block text-cloudza-700">✎ corrected by {step.overriddenBy}</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {parsed.evidence && (parsed.evidence.agent || parsed.evidence.customer) && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
          <span className="text-xs font-medium text-gray-500">Evidence</span>
          {parsed.evidence.agent && (
            <div className="text-xs text-gray-600 italic leading-relaxed">
              <span className="not-italic font-semibold text-gray-500 mr-1">Agent:</span>
              {agentEvidenceChanged ? (
                <span className="inline">
                  "<DiffText
                    oldText={oldParsed.evidence?.agent}
                    newText={newParsed.evidence?.agent}
                    side={isOld ? 'old' : 'new'}
                    inline
                  />"
                </span>
              ) : (
                <>"{parsed.evidence.agent}"</>
              )}
            </div>
          )}
          {parsed.evidence.customer && (
            <div className="text-xs text-gray-600 italic leading-relaxed">
              <span className="not-italic font-semibold text-gray-500 mr-1">Customer:</span>
              {customerEvidenceChanged ? (
                <span className="inline">
                  "<DiffText
                    oldText={oldParsed.evidence?.customer}
                    newText={newParsed.evidence?.customer}
                    side={isOld ? 'old' : 'new'}
                    inline
                  />"
                </span>
              ) : (
                <>"{parsed.evidence.customer}"</>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FieldComparison;
